import React, { useState, useEffect, useCallback } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { API_BASE_URL, EDGE_FUNCTIONS_URL } from '../../constants/api';
import {
  CreditCard,
  CheckCircle,
  AlertCircle,
  Loader2,
  Zap,
  TrendingUp,
  Calendar,
  CalendarDays,
  Receipt,
  Star,
  Clock,
  Shield,
  Users,
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────
interface Plan {
  id: string;
  name: string;
  originalPrice: number;
  amount: number;
  currency: string;
  mode: 'payment' | 'subscription';
  tokens: number | null;
  description: string;
  badge: string | null;
}

interface OrgData {
  id: string;
  name: string;
  billing: {
    plan_type: string;
    tokens_purchased: number;
    tokens_used: number;
    tokens_remaining: number;
    subscription_status: string;
    subscription_end_date: string | null;
  };
  members: Array<{
    user_id: string;
    user_name: string;
    user_email: string;
    role: string;
    total_tokens_used: number;
  }>;
}

interface UsageRecord {
  id: number;
  validation_id: string | null;
  tokens_used: number;
  model_name: string;
  created_at: string;
  user_name: string;
}

interface PaymentRecord {
  id: string;
  amount: number;
  currency: string;
  status: string;
  description: string | null;
  created: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────
const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
};

const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);

const formatTokens = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString();
};

const planIcons: Record<string, React.ReactNode> = {
  starter: <Zap className="w-7 h-7 text-yellow-500" />,
  pro: <TrendingUp className="w-7 h-7 text-purple-500" />,
  monthly: <Calendar className="w-7 h-7 text-blue-500" />,
  yearly: <CalendarDays className="w-7 h-7 text-green-500" />,
};

// ─── Stripe ──────────────────────────────────────────────────────────
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
let stripePromise: Promise<Stripe | null> | null = null;
function getStripePromise() {
  if (!stripePromise && stripeKey) stripePromise = loadStripe(stripeKey);
  return stripePromise;
}

// ─── Checkout Form ───────────────────────────────────────────────────
const CheckoutForm: React.FC<{
  plan: Plan;
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ plan, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const savings = plan.originalPrice - plan.amount;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) { setError(submitError.message || 'Please check your payment details.'); setProcessing(false); return; }

      const { error: stripeError } = await stripe.confirmPayment({
        elements, confirmParams: { return_url: window.location.href }, redirect: 'if_required',
      });

      if (stripeError) { setError(stripeError.message || 'Payment failed.'); setProcessing(false); }
      else { setProcessing(false); onSuccess(); }
    } catch (err: any) {
      setError(err.message || 'Payment failed.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Order Summary */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Plan:</span>
          <span className="font-semibold">{plan.name}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Original price:</span>
          <span className="line-through text-gray-400">{formatCurrency(plan.originalPrice, plan.currency)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Offer price:</span>
          <span className="font-bold text-gray-900">{formatCurrency(plan.amount, plan.currency)}</span>
        </div>
        {savings > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">You save:</span>
            <span className="font-semibold text-green-600">{formatCurrency(savings, plan.currency)}</span>
          </div>
        )}
        {plan.tokens && (
          <div className="flex justify-between text-sm pt-1 border-t border-gray-200">
            <span className="text-gray-600">Tokens:</span>
            <span className="font-semibold">{formatTokens(plan.tokens)}</span>
          </div>
        )}
      </div>

      <PaymentElement />

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={!stripe || processing}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium">
          {processing ? (<><Loader2 className="w-4 h-4 animate-spin" />Processing...</>) : (<><CreditCard className="w-4 h-4" />Pay Now</>)}
        </button>
        <button type="button" onClick={onCancel} disabled={processing}
          className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700">Cancel</button>
      </div>

      <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400 pt-1">
        <Shield className="w-3 h-3" /><span>Secured by Stripe</span>
      </div>
    </form>
  );
};

// ─── Circular Progress ───────────────────────────────────────────────
const CircularProgress: React.FC<{ used: number; total: number }> = ({ used, total }) => {
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const remaining = Math.max(0, total - used);
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  const color = pct > 80 ? '#ef4444' : pct > 50 ? '#eab308' : '#22c55e';

  return (
    <div className="flex items-center gap-4">
      <div className="relative w-28 h-28">
        <svg className="w-28 h-28 -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="8" />
          <circle cx="60" cy="60" r={radius} fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
            className="transition-all duration-700" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-gray-900">{pct.toFixed(0)}%</span>
          <span className="text-[10px] text-gray-500">used</span>
        </div>
      </div>
      <div className="space-y-1">
        <div className="text-sm text-gray-500">Used: <span className="font-semibold text-gray-900">{formatTokens(used)}</span></div>
        <div className="text-sm text-gray-500">Remaining: <span className="font-semibold text-gray-900">{formatTokens(remaining)}</span></div>
        <div className="text-xs text-gray-400">of {formatTokens(total)} total</div>
      </div>
    </div>
  );
};

// ─── Main Billing Page ───────────────────────────────────────────────
const BillingPage: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [org, setOrg] = useState<OrgData | null>(null);
  const [usageHistory, setUsageHistory] = useState<UsageRecord[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [creatingIntent, setCreatingIntent] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [plansRes, orgRes, usageRes, historyRes] = await Promise.all([
        fetch(`${EDGE_FUNCTIONS_URL}/payments/plans`, { headers: authHeaders() }),
        fetch(`${API_BASE_URL}/organizations/my-org`, { headers: authHeaders() }),
        fetch(`${EDGE_FUNCTIONS_URL}/payments/usage-history`, { headers: authHeaders() }),
        fetch(`${EDGE_FUNCTIONS_URL}/payments/history`, { headers: authHeaders() }),
      ]);
      const [plansData, orgData, usageData, historyData] = await Promise.all([
        plansRes.json(), orgRes.json(), usageRes.json(), historyRes.json(),
      ]);
      if (plansData.success) setPlans(plansData.plans);
      if (orgData.success && orgData.data) setOrg(orgData.data);
      if (usageData.success) setUsageHistory(usageData.data);
      if (historyData.success) setPayments(historyData.payments);
    } catch (err) {
      console.error('Failed to load billing data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSelectPlan = async (plan: Plan) => {
    setSelectedPlan(plan);
    setClientSecret(null);
    setCreatingIntent(true);
    try {
      const endpoint = plan.mode === 'payment' ? '/payments/create-payment-intent' : '/payments/create-subscription';
      const res = await fetch(`${EDGE_FUNCTIONS_URL}${endpoint}`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ planId: plan.id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setClientSecret(data.clientSecret);
      if (data.subscriptionId) setSubscriptionId(data.subscriptionId);
    } catch (err: any) {
      alert(err.message || 'Failed to start payment.');
      setSelectedPlan(null);
    } finally {
      setCreatingIntent(false);
    }
  };

  const handlePaymentSuccess = async () => {
    const plan = selectedPlan;
    const subId = subscriptionId;
    setSelectedPlan(null);
    setClientSecret(null);
    setSubscriptionId(null);

    if (plan) {
      try {
        await fetch(`${EDGE_FUNCTIONS_URL}/payments/confirm-payment`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ planId: plan.id, subscriptionId: subId }),
        });
      } catch (err) { console.error('Confirm error:', err); }
    }

    setSuccessMessage(
      plan?.tokens
        ? `${formatTokens(plan.tokens)} tokens have been added to your organization!`
        : `Your unlimited subscription is now active!`
    );
    setShowSuccess(true);
    await loadData();
    setTimeout(() => setShowSuccess(false), 6000);
  };

  const isActivePlan = (planId: string) => {
    if (!org) return false;
    const b = org.billing;
    return b.plan_type === planId && (
      planId === 'starter' || planId === 'pro' ? b.tokens_remaining > 0 : b.subscription_status === 'active'
    );
  };

  if (!stripeKey) {
    return (
      <div className="flex-1 flex flex-col bg-gray-50 overflow-auto p-6">
        <div className="max-w-3xl mx-auto w-full">
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Stripe Not Configured</p>
              <p className="text-sm text-yellow-700 mt-1">Add <code className="bg-yellow-100 px-1 rounded">REACT_APP_STRIPE_PUBLISHABLE_KEY</code> to your frontend .env and restart.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="flex-1 flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  const billing = org?.billing;

  return (
    <div className="flex-1 flex flex-col bg-gray-50 overflow-auto">
      <div className="max-w-5xl mx-auto w-full p-6 space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing & Payments</h1>
          <p className="text-gray-500 mt-1">Manage your organization's plan, tokens, and payment history.</p>
        </div>

        {showSuccess && (
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 p-4 rounded-lg">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <p className="text-green-800 font-semibold">Payment Successful!</p>
              <p className="text-green-700 text-sm">{successMessage}</p>
            </div>
          </div>
        )}

        {/* ── Usage Dashboard ─────────────────────────────────── */}
        {org && billing && billing.plan_type !== 'none' && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <Users className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-semibold text-gray-900">{org.name}</h2>
              <span className="ml-auto inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 capitalize">
                {billing.plan_type}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Token progress or unlimited */}
              <div>
                {(billing.plan_type === 'starter' || billing.plan_type === 'pro') ? (
                  <CircularProgress used={billing.tokens_used} total={billing.tokens_purchased} />
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="w-28 h-28 rounded-full bg-purple-50 flex items-center justify-center">
                      <span className="text-xl font-bold text-purple-600">∞</span>
                    </div>
                    <div className="space-y-1">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-700">Unlimited</span>
                      <div className="text-sm text-gray-500">Tokens used: <span className="font-semibold text-gray-900">{formatTokens(billing.tokens_used)}</span></div>
                      {billing.subscription_end_date && (
                        <div className="text-xs text-gray-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Renews {new Date(billing.subscription_end_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Top 5 users */}
              {org.members && org.members.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Top Users by Token Usage</div>
                  <div className="space-y-1.5">
                    {org.members.slice(0, 5).map((m, i) => (
                      <div key={m.user_id} className="flex items-center gap-2 text-sm">
                        <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500">{i + 1}</span>
                        <span className="flex-1 truncate text-gray-700">{m.user_name}</span>
                        <span className="font-medium text-gray-900">{formatTokens(Number(m.total_tokens_used))}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {(billing.plan_type === 'starter' || billing.plan_type === 'pro') && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button onClick={() => document.getElementById('plans-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                  Add Credits
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Plans ───────────────────────────────────────────── */}
        <div id="plans-section">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Choose a Plan</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((plan) => {
              const isSelected = selectedPlan?.id === plan.id;
              const isActive = isActivePlan(plan.id);
              const savings = plan.originalPrice - plan.amount;
              return (
                <div key={plan.id}
                  className={`relative bg-white border-2 rounded-xl p-5 flex flex-col transition-all ${
                    isActive ? 'border-blue-500 ring-2 ring-blue-200' : isSelected ? 'border-blue-400 ring-1 ring-blue-100' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  {/* Badges */}
                  {plan.badge === 'Best Value' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 px-3 py-0.5 rounded-full text-xs font-semibold bg-green-500 text-white shadow-sm">
                        <Star className="w-3 h-3" /> Best Value
                      </span>
                    </div>
                  )}
                  {plan.badge === 'Most Popular' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 px-3 py-0.5 rounded-full text-xs font-semibold bg-blue-500 text-white shadow-sm">
                        <TrendingUp className="w-3 h-3" /> Most Popular
                      </span>
                    </div>
                  )}
                  {isActive && (
                    <div className="absolute top-2 right-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Active</span>
                    </div>
                  )}

                  <div className="flex items-center gap-3 mb-3 mt-1">
                    {planIcons[plan.id] || <CreditCard className="w-7 h-7 text-gray-400" />}
                    <h3 className="font-semibold text-gray-900 text-sm">{plan.name}</h3>
                  </div>

                  {/* Pricing with strikethrough */}
                  <div className="mb-1">
                    <span className="text-sm line-through text-gray-400 mr-2">{formatCurrency(plan.originalPrice, plan.currency)}</span>
                    {savings > 0 && <span className="text-xs font-medium text-green-600">Save {formatCurrency(savings, plan.currency)}</span>}
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mb-2">
                    {formatCurrency(plan.amount, plan.currency)}
                    {plan.mode === 'subscription' && <span className="text-sm font-normal text-gray-500">/{plan.id === 'yearly' ? 'yr' : 'mo'}</span>}
                  </p>

                  {plan.tokens ? (
                    <div className="mb-3">
                      <div className="text-sm text-gray-600">{formatTokens(plan.tokens)} tokens</div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1"><div className="bg-blue-500 h-1.5 rounded-full" style={{ width: '100%' }} /></div>
                    </div>
                  ) : (
                    <div className="mb-3">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">Unlimited</span>
                    </div>
                  )}

                  <p className="text-xs text-gray-500 mb-4 flex-1">{plan.description}</p>

                  <button onClick={() => handleSelectPlan(plan)} disabled={creatingIntent || isActive}
                    className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? 'bg-blue-100 text-blue-700 cursor-default' : isSelected ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}>
                    {isActive ? 'Current Plan' : creatingIntent && isSelected ? (
                      <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Preparing...</span>
                    ) : 'Select Plan'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Stripe Checkout ─────────────────────────────────── */}
        {clientSecret && selectedPlan && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Complete Payment</h2>
            <p className="text-sm text-gray-500 mb-4">{selectedPlan.name}</p>
            <Elements stripe={getStripePromise()} options={{ clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: '#2563eb' } } }}>
              <CheckoutForm plan={selectedPlan} onSuccess={handlePaymentSuccess} onCancel={() => { setSelectedPlan(null); setClientSecret(null); }} />
            </Elements>
          </div>
        )}

        {/* ── Usage History ────────────────────────────────────── */}
        {usageHistory.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-gray-400" />Token Usage History
            </h2>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">User</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Tokens</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Model</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {usageHistory.slice(0, 20).map(u => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700">{new Date(u.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-700">{u.user_name || '—'}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{u.tokens_used.toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs">{u.model_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Payment History ──────────────────────────────────── */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-gray-400" />Payment History
          </h2>
          {payments.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
              <CreditCard className="w-10 h-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">No payments yet.</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Amount</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {payments.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700">{new Date(p.created).toLocaleDateString()}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{formatCurrency(p.amount, p.currency)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${p.status === 'succeeded' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{p.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Test Cards ──────────────────────────────────────── */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">Stripe Test Mode</h3>
          <div className="text-xs text-blue-700 space-y-1">
            <p><strong>Success:</strong> 4242 4242 4242 4242 — Any future date, any CVC</p>
            <p><strong>Decline:</strong> 4000 0000 0000 0002</p>
            <p><strong>3D Secure:</strong> 4000 0025 0000 3155</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
